'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

export default function RefundPolicyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[#F59E0B]/10 blur-[120px]" />
          <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-[#0D9488]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30">
            <svg className="w-4 h-4 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            <span className="text-amber-300 text-sm font-bold uppercase tracking-widest">Refund Policy</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            Fair refunds.<br />
            <span className="text-[#4DD9CC]">No runaround.</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Our escrow system protects your money. Here's exactly when you get a refund, how much, and how fast.
          </p>
          <div className="flex items-center justify-center gap-6 pt-2">
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Processing Time</div>
              <div className="text-sm text-slate-400">1–5 business days</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Dispute Window</div>
              <div className="text-sm text-slate-400">48 hours after delivery</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Last Updated</div>
              <div className="text-sm text-slate-400">July 14, 2026</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>
      {/* Quick summary */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: '✅', title: 'Full Refund', desc: 'Traveler cancels after accepting. Parcel lost or never delivered. Traveler fails to show up.', color: 'bg-green-50 border-green-200 text-green-700' },
              { icon: '⚡', title: 'Partial Refund', desc: 'Parcel damaged in transit. Delivery significantly delayed beyond agreed time. Partial delivery.', color: 'bg-amber-50 border-amber-200 text-amber-700' },
              { icon: '❌', title: 'No Refund', desc: 'Sender cancels after traveler has departed. Parcel refused by recipient. Prohibited items seized.', color: 'bg-red-50 border-red-200 text-red-700' },
            ]?.map((card) => (
              <div key={card?.title} className={`rounded-2xl p-6 border ${card?.color} hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <div className="text-3xl mb-3">{card?.icon}</div>
                <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{card?.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{card?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Main content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

          {/* How Escrow Works */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl">🏦</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">How Our Escrow System Protects You</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">MuyuRide uses an escrow payment system — your money is never paid directly to the traveler until you confirm delivery. This means you're always protected.</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {[
                { step: '1', label: 'You Pay', desc: 'Funds deducted from M-Pesa or card and held securely by MuyuRide', color: 'bg-blue-50 border-blue-100' },
                { step: '2', label: 'Delivery Happens', desc: 'Traveler picks up and delivers your parcel', color: 'bg-teal-50 border-teal-100' },
                { step: '3', label: 'You Confirm', desc: 'You confirm receipt — or dispute within 48 hours', color: 'bg-amber-50 border-amber-100' },
                { step: '4', label: 'Funds Released', desc: 'Traveler paid. Or refund issued to you.', color: 'bg-green-50 border-green-100' },
              ]?.map((item) => (
                <div key={item?.step} className={`rounded-xl p-5 border ${item?.color} text-center`}>
                  <div className="w-8 h-8 rounded-full bg-[#0D9488] text-white text-sm font-extrabold flex items-center justify-center mx-auto mb-3">{item?.step}</div>
                  <div className="font-extrabold text-[#0F172A] text-sm mb-1">{item?.label}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{item?.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Full Refund Scenarios */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-xl">✅</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">Full Refund Scenarios</h2>
            </div>
            <p className="text-slate-600 mb-5 leading-relaxed">You are entitled to a <strong>100% refund</strong> of the delivery fee in the following situations:</p>
            <div className="space-y-3">
              {[
                {
                  scenario: 'Traveler Cancels After Acceptance',
                  detail: 'If a traveler cancels a confirmed delivery after accepting your order, you receive a full refund of the delivery fee within 24 hours. The traveler\'s account is flagged and their trust score is reduced.',
                  timeline: '24 hours',
                },
                {
                  scenario: 'Parcel Not Delivered',
                  detail: 'If the traveler marks a delivery as complete but you never received your parcel, file a dispute within 48 hours. After investigation, if the non-delivery is confirmed, you receive a full refund plus a KES 200 inconvenience credit.',
                  timeline: '5 business days',
                },
                {
                  scenario: 'Traveler No-Show at Pickup',
                  detail: 'If the traveler fails to appear at the agreed pickup location within 2 hours of the scheduled time without prior notice, the order is automatically cancelled and you receive a full refund.',
                  timeline: '24 hours',
                },
                {
                  scenario: 'Parcel Lost in Transit',
                  detail: 'If your parcel is confirmed lost (traveler cannot produce the parcel or proof of delivery), you receive a full refund of the delivery fee plus compensation up to the declared parcel value (maximum KES 50,000 without additional insurance).',
                  timeline: '5 business days',
                },
                {
                  scenario: 'Platform Error or Double Charge',
                  detail: 'If you are charged twice due to a platform error, or if a payment is processed for an order that was never created, you receive a full refund of the duplicate charge within 1 business day.',
                  timeline: '1 business day',
                },
                {
                  scenario: 'Order Cancelled Before Traveler Accepts',
                  detail: 'If you cancel your order before any traveler has accepted it, you receive a full refund of any pre-authorization hold. No cancellation fee applies.',
                  timeline: 'Immediate',
                },
              ]?.map((item) => (
                <div key={item?.scenario} className="rounded-xl border border-green-100 bg-green-50/30 p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-extrabold text-[#0F172A] text-sm">{item?.scenario}</h3>
                    <span className="shrink-0 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">{item?.timeline}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{item?.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Partial Refund Scenarios */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-xl">⚡</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">Partial Refund Scenarios</h2>
            </div>
            <p className="text-slate-600 mb-5 leading-relaxed">In these situations, you may receive a <strong>partial refund</strong> based on the circumstances and our investigation:</p>
            <div className="space-y-3">
              {[
                {
                  scenario: 'Parcel Damaged in Transit',
                  detail: 'If your parcel arrives damaged and the damage was caused by the traveler\'s negligence (not pre-existing or due to inadequate packaging), you may receive a partial refund of the delivery fee (25–100%) plus compensation for the damaged item up to its declared value.',
                  refund: '25–100% of delivery fee + item compensation',
                },
                {
                  scenario: 'Significant Delivery Delay',
                  detail: 'If delivery is delayed by more than 24 hours beyond the agreed delivery date without a valid reason (e.g., road closure, emergency), you may receive a 25–50% refund of the delivery fee.',
                  refund: '25–50% of delivery fee',
                },
                {
                  scenario: 'Partial Delivery',
                  detail: 'If you sent multiple items and only some were delivered, you receive a proportional refund based on the declared value of the undelivered items.',
                  refund: 'Proportional to undelivered items',
                },
                {
                  scenario: 'Sender Cancels After Traveler Departs',
                  detail: 'If you cancel an order after the traveler has already departed for the pickup location, a cancellation fee of 30% of the delivery fee is charged to compensate the traveler for their time and travel costs.',
                  refund: '70% of delivery fee',
                },
              ]?.map((item) => (
                <div key={item?.scenario} className="rounded-xl border border-amber-100 bg-amber-50/30 p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-extrabold text-[#0F172A] text-sm">{item?.scenario}</h3>
                    <span className="shrink-0 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold text-right max-w-[140px]">{item?.refund}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{item?.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* No Refund Scenarios */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-xl">❌</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">No Refund Scenarios</h2>
            </div>
            <p className="text-slate-600 mb-5 leading-relaxed">Refunds are <strong>not available</strong> in the following circumstances:</p>
            <div className="space-y-3">
              {[
                { scenario: 'Parcel Refused by Recipient', detail: 'If the intended recipient refuses to accept the parcel without a valid reason, the delivery fee is non-refundable. The traveler has fulfilled their obligation by delivering to the correct address.' },
                { scenario: 'Prohibited Items Seized', detail: 'If your parcel is seized by authorities because it contained prohibited items, no refund is issued. You may also be liable for any fines or legal costs incurred by the traveler.' },
                { scenario: 'Incorrect Address Provided', detail: 'If you provided an incorrect pickup or delivery address and the traveler made a good-faith attempt to deliver, no refund is issued. You may negotiate a re-delivery with the traveler at an additional cost.' },
                { scenario: 'Damage Due to Inadequate Packaging', detail: 'If a parcel is damaged because it was not packaged adequately for the journey (e.g., fragile items not padded, liquids not sealed), no refund is issued for the damage.' },
                { scenario: 'Change of Mind After Delivery', detail: 'Once a delivery is confirmed by the sender, the transaction is final. We cannot process refunds for change of mind after confirmation.' },
                { scenario: 'Platform Service Fees', detail: 'MuyuRide\'s service fee (8–12% of the delivery amount) is non-refundable in all circumstances, as it covers platform operations, verification, and dispute resolution services.' },
              ]?.map((item) => (
                <div key={item?.scenario} className="rounded-xl border border-red-100 bg-red-50/30 p-5">
                  <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{item?.scenario}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item?.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How to Request */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-xl">📋</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">How to Request a Refund</h2>
            </div>
            <div className="space-y-4">
              {[
                { step: '1', title: 'File a Dispute', desc: 'Go to your order in the Active Deliveries or Order History section. Click "File Dispute" within 48 hours of the delivery date. Describe the issue and upload any supporting evidence (photos, screenshots, messages).', icon: '📝' },
                { step: '2', title: 'Investigation Begins', desc: 'Our Trust & Safety team reviews your dispute within 24 hours. We may contact both parties for additional information. All communications are logged and time-stamped.', icon: '🔍' },
                { step: '3', title: 'Decision Issued', desc: 'We issue a binding decision within 5 business days. Both parties are notified by email and in-app notification. The decision includes the refund amount (if any) and the reasoning.', icon: '⚖️' },
                { step: '4', title: 'Refund Processed', desc: 'Approved refunds are processed to your original payment method: M-Pesa refunds within 24 hours, card refunds within 3–5 business days (depending on your bank).', icon: '💸' },
              ]?.map((item) => (
                <div key={item?.step} className="flex gap-4 p-5 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/20 transition-all duration-200">
                  <div className="w-10 h-10 rounded-xl bg-[#0D9488] text-white font-extrabold flex items-center justify-center shrink-0 text-lg">{item?.icon}</div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-[#0D9488] uppercase tracking-wider">Step {item?.step}</span>
                    </div>
                    <h3 className="font-extrabold text-[#0F172A] text-sm mb-1">{item?.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Insurance */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-xl">🛡️</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">Parcel Insurance</h2>
            </div>
            <p className="text-slate-600 mb-5 leading-relaxed">For high-value parcels, we recommend purchasing additional insurance through MuyuRide. Standard coverage is limited to KES 50,000 per delivery.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { plan: 'Basic', coverage: 'Up to KES 50,000', price: 'Included free', features: ['Lost parcel', 'Non-delivery', 'Traveler cancellation'], color: 'border-slate-200 bg-white' },
                { plan: 'Standard', coverage: 'Up to KES 150,000', price: 'KES 150 per delivery', features: ['All Basic coverage', 'Parcel damage', 'Delay compensation'], color: 'border-teal-200 bg-teal-50' },
                { plan: 'Premium', coverage: 'Up to KES 500,000', price: 'KES 400 per delivery', features: ['All Standard coverage', 'Cold-chain failures', 'Electronics coverage', 'Priority dispute resolution'], color: 'border-amber-200 bg-amber-50' },
              ]?.map((plan) => (
                <div key={plan?.plan} className={`rounded-xl border p-5 ${plan?.color}`}>
                  <div className="font-extrabold text-[#0F172A] text-lg mb-1">{plan?.plan}</div>
                  <div className="text-2xl font-extrabold text-[#0D9488] mb-1">{plan?.coverage}</div>
                  <div className="text-xs text-slate-500 mb-4">{plan?.price}</div>
                  <ul className="space-y-1.5">
                    {plan?.features?.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                        <svg className="w-3.5 h-3.5 text-[#0D9488] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">❓</div>
              <h2 className="text-2xl font-extrabold text-[#0F172A]">Refund FAQs</h2>
            </div>
            <div className="space-y-3">
              {[
                { q: 'How long does a refund take?', a: 'M-Pesa refunds are processed within 24 hours of approval. Card refunds take 3–5 business days depending on your bank. You\'ll receive an SMS and email confirmation when the refund is initiated.' },
                { q: 'Can I appeal a dispute decision?', a: 'Yes. If you disagree with our decision, you may submit an appeal within 7 days by emailing support@muyulink.com with new evidence. Appeals are reviewed by a senior Trust & Safety officer within 3 business days.' },
                { q: 'What if the traveler disputes my claim?', a: 'Both parties have the opportunity to present evidence. We review all evidence impartially — messages, photos, GPS data, and delivery records. Our decision is based on the totality of evidence.' },
                { q: 'Is the platform fee ever refunded?', a: 'The platform service fee (8–12%) is non-refundable in all circumstances. It covers the cost of verification, escrow management, and dispute resolution services that were provided regardless of the delivery outcome.' },
                { q: 'What if my parcel is worth more than KES 50,000?', a: 'Standard coverage is capped at KES 50,000. For higher-value items, purchase our Standard (KES 150,000) or Premium (KES 500,000) insurance plan when creating your order.' },
                { q: 'Can I get a refund if I change my mind?', a: 'You can cancel for a full refund before any traveler accepts your order. After acceptance, cancellation fees apply (see Partial Refund section). After delivery confirmation, no refunds are available.' },
              ]?.map((faq, i) => (
                <div key={i} className={`rounded-xl border transition-all duration-300 overflow-hidden ${openFaq === i ? 'border-[#0D9488]/30 bg-teal-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                    <span className="font-bold text-[#0F172A] text-sm leading-snug">{faq?.q}</span>
                    <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${openFaq === i ? 'bg-[#0D9488] rotate-45' : 'bg-slate-100'}`}>
                      <svg className={`w-4 h-4 ${openFaq === i ? 'text-white' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-slate-600 leading-relaxed">{faq?.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-2xl bg-gradient-to-br from-[#0A1628] to-[#0D2B3E] p-8 text-center space-y-4">
            <div className="text-4xl">💬</div>
            <h2 className="text-2xl font-extrabold text-white">Still have questions?</h2>
            <p className="text-slate-300 max-w-md mx-auto">Our support team is available Monday–Friday, 8am–6pm EAT. We typically respond within 4 hours.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link href="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0D9488] text-white font-bold hover:bg-[#0F766E] transition-all duration-200">
                Contact Support →
              </Link>
              <Link href="/help-center" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition-all duration-200">
                Visit Help Center
              </Link>
            </div>
          </div>

        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
